import React from 'react';
import classNames from 'classnames';
import FontAwesomeIcon from 'src/components/Icon';

interface BaseProps {
  className?: string;
  children?: React.ReactNode;
}

interface PaginationLinkProps extends BaseProps {
  isActive?: boolean;
  disabled?: boolean;
  ariaLabel?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

interface PaginationNavProps extends BaseProps {
  disabled?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

export const Pagination = ({ className, children }: BaseProps) => {
  const navClassName: string = classNames('cp-nav', className);

  return (
    <nav role="navigation" aria-label="pagination" className={navClassName}>
      {children}
    </nav>
  );
};

export const PaginationContent = ({ className, children }: BaseProps) => {
  const ulClassName: string = classNames('cp-content', className);

  return <ul className={ulClassName}>{children}</ul>;
};

export const PaginationItem = ({ className, children }: BaseProps) => {
  const liClassName: string = classNames('cp-item', className);

  return <li className={liClassName}>{children}</li>;
};

export const PaginationLink = ({
  className,
  isActive,
  disabled,
  ariaLabel,
  onClick,
  children,
}: PaginationLinkProps) => {
  const buttonClassName: string = classNames(
    'cp-link',
    {
      'cp-link-active': isActive,
      'cp-link-disabled': disabled,
    },
    className
  );

  return (
    <button
      type="button"
      className={buttonClassName}
      aria-label={ariaLabel}
      aria-current={isActive ? 'page' : undefined}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

export const PaginationPrevious = ({
  className,
  disabled,
  onClick,
}: PaginationNavProps) => {
  return (
    <PaginationLink
      className={classNames('cp-link-nav', className)}
      ariaLabel="Go to previous page"
      disabled={disabled}
      onClick={onClick}
    >
      <FontAwesomeIcon glyph="chevron-left" weight="solid" />
      <span className="cp-link-nav-label">Previous</span>
    </PaginationLink>
  );
};

export const PaginationNext = ({
  className,
  disabled,
  onClick,
}: PaginationNavProps) => {
  return (
    <PaginationLink
      className={classNames('cp-link-nav', className)}
      ariaLabel="Go to next page"
      disabled={disabled}
      onClick={onClick}
    >
      <span className="cp-link-nav-label">Next</span>
      <FontAwesomeIcon glyph="chevron-right" weight="solid" />
    </PaginationLink>
  );
};

export const PaginationJumpToBeginning = ({
  className,
  disabled,
  onClick,
}: PaginationNavProps) => {
  return (
    <PaginationLink
      className={classNames('cp-link-jump', className)}
      ariaLabel="Go to first page"
      disabled={disabled}
      onClick={onClick}
    >
      <FontAwesomeIcon glyph="angle-double-left" weight="solid" />
    </PaginationLink>
  );
};

export const PaginationJumpToEnd = ({
  className,
  disabled,
  onClick,
}: PaginationNavProps) => {
  return (
    <PaginationLink
      className={classNames('cp-link-jump', className)}
      ariaLabel="Go to last page"
      disabled={disabled}
      onClick={onClick}
    >
      <FontAwesomeIcon glyph="angle-double-right" weight="solid" />
    </PaginationLink>
  );
};

export const PaginationEllipsis = ({ className }: BaseProps) => {
  const spanClassName: string = classNames('cp-ellipsis', className);

  return (
    <span className={spanClassName} aria-hidden="true">
      &hellip;
      <span className="cp-sr-only">More pages</span>
    </span>
  );
};
